/**
 * 自动提取的合同表格（只读展示）。
 *
 * 【为什么只读】
 * 提取是启发式的（按文本块坐标聚类列），合同版式千变万化，不可能 100% 准。
 * 产品上定位为"辅助阅读"——**以原件为准**，因此不做单元格编辑：
 * 可编辑会让人以为数据是准的，进而直接引用。
 *
 * 【为什么必须显示置信度与空单元格数】
 * 与其声称"识别到一个表格"，不如说"识别到一个表格，置信度 71%，
 * 有 3 个空单元格（原件中可能是合并单元格）"。用户据此决定要不要翻原件核对。
 */

import { Table, Tag, Typography, Space, Empty, Alert } from 'antd';
import type { ContractTable } from '@ilc/shared';

export interface TablePreviewProps {
  tables: ContractTable[] | null | undefined;
}

function confidenceColor(c: number): string {
  if (c >= 75) return 'green';
  if (c >= 50) return 'orange';
  return 'red';
}

export function TablePreview({ tables }: TablePreviewProps): React.ReactElement {
  if (!tables || tables.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="未在合同正文中检测到表格结构"
      />
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Alert
        type="info"
        showIcon
        message="表格为自动识别结果，可能存在误差"
        description="请以合同原件为准。点击上方「查看原件」可打开附件对照。"
      />

      {tables.map((t, idx) => {
        const colCount = t.headers.length || 1;
        /*
         * 用单元格数组直接做 dataSource：合并列通过 `colSpan` 表达，
         * 被覆盖的列在 rows 里仍然占位（text 为空），因此每行列数一致。
         */
        const dataSource = t.rows.map((cells, ri) => ({ key: ri, cells }));

        const firstPage = t.pages[0] ?? t.pageIndex;

        return (
          <div key={idx}>
            <Space size={8} style={{ marginBottom: 6 }} wrap>
              <Typography.Text strong>
                表 {idx + 1}
                {t.caption ? ` · ${t.caption}` : ''}
              </Typography.Text>
              {t.pages.length > 1 ? (
                <Tag>第 {firstPage + 1}-{(t.pages[t.pages.length - 1] ?? firstPage) + 1} 页（跨页）</Tag>
              ) : (
                <Tag>第 {firstPage + 1} 页</Tag>
              )}
              <Tag color={confidenceColor(t.confidence)}>识别置信度 {t.confidence}</Tag>
              {t.headerInferred ? (
                <Tag color="orange">续页表头未能确认，按上一页列结构推断</Tag>
              ) : null}
            </Space>

            <Table
              size="small"
              bordered
              pagination={false}
              scroll={{ x: 'max-content' }}
              rowKey="key"
              dataSource={dataSource}
              columns={Array.from({ length: colCount }, (_, ci) => ({
                title: t.headers[ci] ?? '',
                key: `c${ci}`,
                // 数值列右对齐：财务用户的阅读习惯，也让"这列是金额"在视觉上自证
                align: t.numericColumns[ci] ? ('right' as const) : ('left' as const),
                render: (_: unknown, row: { cells: ContractTable['rows'][number] }) => {
                  const cell = row.cells[ci];
                  if (!cell) return '';
                  if (cell.colSpan === 0) return null; // 被左侧合并覆盖
                  return {
                    children: <span className={t.numericColumns[ci] ? 'ilc-mono' : undefined}>{cell.text}</span>,
                    props: cell.colSpan && cell.colSpan > 1 ? { colSpan: cell.colSpan } : {},
                  };
                },
              }))}
            />

            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              共 {t.rows.length} 行 × {colCount} 列
              {t.emptyCells > 0
                ? `，其中 ${t.emptyCells} 个空单元格（原件中可能是合并单元格）`
                : ''}
              。表格为自动识别结果，一切以原件为准。
            </Typography.Text>
          </div>
        );
      })}
    </Space>
  );
}

export default TablePreview;
